import Icon from "@hudson/ui/components/Icon";
import PulseLoader from "@hudson/ui/components/Loader/PulseLoader";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormState,
} from "@hudson/ui/components/ReactHookForm";
import styles from "@hudson/ui/styles/Form.module.css";
import Session from "@lib/session";
import useConfig from "@lib/use_config";
import { motion } from "framer-motion";
import { useEffect } from "react";
import CustomerRequestToggle from "./CustomerRequestToggle";
import PickupAndDropoff from "./PickupAndDropoff";
import PickupOrFlightDateTime from "./PickupOrFlightDateTime";
import PudPersonSelector from "./PudPersonSelector";

interface UpfrontProps {
  className?: string;
  fieldNames: { customerRequest: string; datetime: string; pickup: string; dropoff: string };
  findLocations?: be.FindLocationsFunction;
  hideSubmit?: boolean;
  limitLocations?: number;
  locationPredictionRenderer?: JSX.Element;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noMap?: boolean;
  onFormCompleteChange: (complete: boolean) => void;
  onSubmit: (data: be.LandingSubmitData) => void;
  values: be.LandingData;
  submitting?: boolean;
}

function UpfrontCollection({
  className,
  fieldNames,
  findLocations,
  hideSubmit = false,
  limitLocations,
  locationPredictionRenderer,
  locationPredictionSelected,
  noMap = false,
  onFormCompleteChange,
  onSubmit,
  submitting,
  values,
}: UpfrontProps): JSX.Element {
  const { data: cfgData } = useConfig(["customerRequestMode", "openReservationMode"]);
  const { customerRequestMode, openReservationMode } = cfgData?.config || {};

  const formMethods = useForm({ defaultValues: values });
  const { isDirty, isValid } = useFormState({ control: formMethods.control });

  useEffect(() => {
    onFormCompleteChange(!isDirty && isValid);
  }, [isDirty, isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  const [customerRequest, pickup, dropoff, pudPersonId] = formMethods.watch([
    fieldNames.customerRequest,
    fieldNames.pickup,
    fieldNames.dropoff,
    "pudPerson",
  ]);

  // When pudPersonId is updated, update the session
  useEffect(() => {
    async function updatePudPerson() {
      Session.updateBooking({ pudPersonId: pudPersonId as string | undefined });
      await global.window.apolloClient.clearStore();
    }
    if (pudPersonId != Session.getBooking().pudPersonId) updatePudPerson();
  }, [pudPersonId]);

  const openResCollectDate = openReservationMode === "enabled_collect_date";
  const forceCustomerRequest = openResCollectDate;
  const isDateOnlyMode = customerRequestMode === 2;
  let defaultTime;
  if (openResCollectDate || isDateOnlyMode) {
    defaultTime = pickup?.typeString === "airport" ? "00:00" : "23:59";
  }

  const haveAirport = [pickup?.typeString, dropoff?.typeString].includes("airport");
  let flightType: "arrival" | "departure" | undefined = undefined;
  if (haveAirport) flightType = pickup?.typeString === "airport" ? "arrival" : "departure";

  const submitForm: SubmitHandler<be.LandingSubmitData> = (data: be.LandingSubmitData) => {
    onSubmit(data);
    formMethods.reset(data, { keepValues: true });
  };

  const divClass = `flex normal:flex-row flex-col ${className ? className : ""}`;
  const showDates =
    openResCollectDate || isDateOnlyMode || (!Session.getCurrentLeg().open && openReservationMode != "enabled");

  return (
    <FormProvider {...formMethods}>
      <form id="landing-form" onSubmit={formMethods.handleSubmit(submitForm)}>
        {!Session.isSecondLeg() && <PudPersonSelector className="mb-4 max-w-full w-80" />}

        <motion.div className={divClass} layout>
          <PickupAndDropoff
            className="min-w-0 mb-3 normal:mb-0 normal:mr-3"
            fieldNames={fieldNames}
            findLocations={findLocations}
            limitLocations={limitLocations}
            locationPredictionRenderer={locationPredictionRenderer}
            locationPredictionSelected={locationPredictionSelected}
            noMap={noMap}
          />
          {showDates && (
            <div className="flex flex-col-reverse items-start sm:flex-col gap-y-0">
              <PickupOrFlightDateTime
                className="self-start w-5/6 min-w-0 normal:w-auto"
                fieldName={fieldNames.datetime}
                flightType={flightType}
                hideIcon={forceCustomerRequest || customerRequestMode > 0}
                hideTime={openResCollectDate || isDateOnlyMode}
                isFlight={!isDateOnlyMode && !forceCustomerRequest && haveAirport && !customerRequest}
                timeDefault={defaultTime}
              />
              {!forceCustomerRequest && haveAirport && customerRequestMode === 1 && (
                <CustomerRequestToggle
                  className="mt-2 max-w-[340px]"
                  fieldName={fieldNames.customerRequest}
                  key={String(customerRequest)}
                />
              )}
            </div>
          )}
          {!hideSubmit && (
            <motion.button
              aria-label="Continue"
              disabled={submitting}
              layout
              type="submit"
              className={`${styles.button} normal:h-12 inline-flex normal:self-start self-end items-center p-2 normal:px-3 px-8 border border-transparent rounded-md shadow-sm normal:ml-3 disabled:opacity-50 bg-[--primary-color] hover:bg-[--primary-hover]`}
            >
              {submitting ? (
                <PulseLoader colorVar="primary-text" />
              ) : (
                <Icon className="w-8 h-8" colorVar="--primary-text" type="rightArrow" />
              )}
            </motion.button>
          )}
        </motion.div>
      </form>
    </FormProvider>
  );
}

export default UpfrontCollection;
